import { OrderListRow } from "../models/orderListModels";

export const filterOrderListRows = (
  rows: OrderListRow[],
  categories: string[] | null,
  ingredients: string[] | null
): OrderListRow[] => {
  const categorySet = categories ? new Set(categories.map(value => value.toLowerCase())) : null;
  const ingredientSet = ingredients ? new Set(ingredients.map(value => value.toLowerCase())) : null;
  return rows
    .filter(row =>
      (categorySet === null || categorySet.has(row.category.toLowerCase())) &&
      (ingredientSet === null || ingredientSet.has(row.ingredient.toLowerCase()))
    )
    .sort((left, right) =>
      left.category.localeCompare(right.category, undefined, { sensitivity: "base" }) ||
      left.ingredient.localeCompare(right.ingredient, undefined, { sensitivity: "base" })
    );
};

// Ingredient options always come from the original rows filtered by category alone, never from
// an already ingredient-filtered subset, so they stay dependent on category without compounding.
export const getIngredientOptionsForCategories = (
  rows: OrderListRow[],
  categories: string[] | null
): string[] => Array.from(new Set(filterOrderListRows(rows, categories, null).map(row => row.ingredient)))
  .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));

// A selection that becomes fully invalid (categories narrowed, or restoring a persisted value
// against new data) reverts to "All" rather than leaving an explicit-but-empty selection.
export const pruneUnavailableSelections = (
  selection: string[] | null,
  availableOptions: string[]
): string[] | null => {
  if (selection === null) {
    return null;
  }
  const available = new Set(availableOptions.map(option => option.toLowerCase()));
  const pruned = selection.filter(value => available.has(value.toLowerCase()));
  if (pruned.length === 0) {
    return null;
  }
  // Preserve the original reference when nothing was removed, so callers can bail out of re-rendering.
  return pruned.length === selection.length ? selection : pruned;
};

export const pruneIngredientsForCategories = (
  rows: OrderListRow[],
  categories: string[] | null,
  ingredients: string[] | null
): string[] | null => pruneUnavailableSelections(ingredients, getIngredientOptionsForCategories(rows, categories));

// While searching, checking "select all" replaces the draft with only the visible results rather
// than merging with whatever was already selected; toggling again clears the draft entirely.
export const toggleSelectAllOptions = (
  currentSelection: Set<string>,
  visibleOptions: string[]
): Set<string> => {
  const allVisibleSelected = visibleOptions.length > 0 && visibleOptions.every(option => currentSelection.has(option));
  return allVisibleSelected ? new Set<string>() : new Set(visibleOptions);
};

// An untouched "everything selected" draft (the default when nothing is applied yet) combined
// with an active search means the user is picking from the search results, not confirming a
// no-op "All" - apply just what's visible rather than the full, mostly-hidden draft.
export const resolveAppliedSelection = (
  draft: Set<string>,
  options: string[],
  visibleOptions: string[],
  hasActiveSearch: boolean
): string[] | null => {
  const isPristineFullSelection = options.length > 0 && options.every(option => draft.has(option));
  const effectiveSelection = isPristineFullSelection && hasActiveSearch ? visibleOptions : Array.from(draft);
  return effectiveSelection.length === options.length ? null : effectiveSelection;
};